// The Workspace board's batch approval, as pure functions over the batch rows the
// board already builds (each row: registering / failed / hasAnalysing flags and a
// plan of approvable members and skipped ones).

// Why a batch cannot be approved right now, or '' when it can. Order matters: a
// boundary the transfer abandoned is not "still uploading".
export const batchBlockedReason = (row) => {
  if (row.failed) return 'This upload did not finish — delete its files and re-upload.';
  if (row.registering) return 'Some files in this batch are still uploading.';
  if (row.hasAnalysing) return 'Some files in this batch are still being analysed.';
  return '';
};

// The one plan behind Approve ready: the button's count and hover list, the
// confirmation and the commit all read it, so none can disagree about what the
// action touches. `prepare` shapes a ready row for the confirmation. A settled
// batch with no file ready is counted as unready and left alone, not dropped
// from view (M217).
export const buildReadyPlan = (batchRows, prepare) => {
  const settled = batchRows.filter((row) => !batchBlockedReason(row));
  const batches = settled.filter((row) => row.plan.approvable.length > 0).map(prepare);
  return {
    batches,
    readyCount: batches.reduce((sum, batch) => sum + batch.plan.approvable.length, 0),
    blockedBatches: batchRows.length - settled.length,
    unreadyBatches: settled.length - batches.length,
  };
};

// What the hover shows: each file the plan approves with its destinations, under
// its batch, capped so a large board does not become a wall of text.
export const readyHoverLines = (readyPlan, destinationsOf, cap = 12) => {
  const lines = [];
  let shown = 0;
  let remaining = 0;
  readyPlan.batches.forEach((batch) => {
    if (shown < cap) lines.push(`Batch ${batch.batchNo}`);
    batch.plan.approvable.forEach(({ file, action }) => {
      if (shown < cap) {
        lines.push(`${file.fileName} → ${destinationsOf(action)}`);
        shown += 1;
      } else {
        remaining += 1;
      }
    });
  });
  if (remaining > 0) lines.push(`and ${remaining} more`);
  return lines;
};

// Why the button is disabled when nothing is ready.
export const noReadyReason = (readyPlan) => (
  readyPlan.blockedBatches > 0
    ? 'No batches are ready to allocate — files are still uploading or being analysed.'
    : 'No files are ready to allocate — choose a destination table first.'
);

// The stored batch state is shared by every browser, unlike a local upload flag.
export const uploadApprovalBlockedReason = (upload) => {
  if (!upload) return 'Upload status is still loading.';
  if (upload.status === 'registering') return 'Some files in this batch are still uploading.';
  if (upload.status !== 'uploaded') return 'This upload is no longer available for routing.';
  return '';
};
